/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-23 09:54:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  PageActions,
  Button,
  TextStyle,
  SkeletonDisplayText,
  SkeletonBodyText,
  Stack
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import {
  Field,
  FieldArray,
  reduxForm,
  getFormValues,
  change,
  SubmissionError,
  reset
} from 'redux-form';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import OptionsTable from 'src/components/views/filter-tree/item/options-table';
import InfoBanner from 'src/components/views/filter-tree/item/info-banner';
import MainMenu from 'src/components/containers/main-menu';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import { isRequired, hasTag } from 'src/helpers/form-validation-helper';
import { prettyText, confirmLeavePage } from 'src/helpers';
import { RenderAutocomplete } from 'src/components/views/common/autocomplete-hoc';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';

import { LIMIT_FILTER_OPTIONS } from 'src/constants/filter-tree';
import { routes } from 'src/routes';

import * as FilterTreeActions from 'actions/filter-tree/filter-tree-actions';
import * as CollectionActions from 'actions/filter-tree/collection-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as MultiLevelActions from 'actions/filter-tree/multi-level-actions';
import * as managementActions from 'actions/filter-tree/management-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type { FilterTreeType } from 'src/models/filter-tree';
import type { ComposedProps, State } from './index.type';


const isRequiredValue = isRequired();
class FilterTreeItem extends Component<ComposedProps, State> {
  state = {
    collections: [],

    // Modal send request result
    activeRequestModal: false,
    activeModalResult: false,

    // Display schedule sync info
    scheduleSyncIn: 0
  };

  componentDidMount() {
    const { getFilterTree, getFilterData, getAvailableCollections } = this.props;
    const { filterTreeId } = this.props.match.params;

    getFilterData();
    getAvailableCollections();

    if (!!filterTreeId) {
      getFilterTree(filterTreeId);
    }

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps: ComposedProps) {
    const { filterTreeId } = this.props.match.params;
    const { filterTreeId: nextFilterTreeId } = nextProps.match.params;

    // Run when redirect from new to edit
    if (!!nextFilterTreeId && filterTreeId !== nextFilterTreeId) {
      const { getFilterTree, getFilterData, getAvailableCollections } = this.props;

      this.setState({ collections: [] });
      getFilterData();
      getAvailableCollections();
      getFilterTree(nextFilterTreeId);
    }

    const { collectionsSuccess, success } = this.props;
    const {
      availableCollections: nextAvailableCollections,
      filterTreeCollection: nextFilterTreeCollection,
      collectionsSuccess: nextCollectionsSuccess,
      success: nextSuccess
    } = nextProps;

    if (collectionsSuccess !== nextCollectionsSuccess && nextCollectionsSuccess === true) {
      this.setState({ collections: [...this.state.collections, ...nextAvailableCollections] });
    }

    if (success !== nextSuccess && nextSuccess === true) {
      this.setState({ collections: [...nextFilterTreeCollection, ...this.state.collections] });
    }

    // Show schedule sync toast
    this.showScheduleSyncInfo(nextProps);
  }

  showScheduleSyncInfo = nextProps => {
    const { waitSyncAt, scheduleSyncIn, loadingSave } = nextProps;
    const { updateShowBanner, getSyncProcessStatus, loadingSave: loadingSavePrev } = this.props;

    // Check to show banner Processing update when change status/ import
    if (loadingSavePrev && !loadingSave) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;

        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }

      // Call sync process status
      getSyncProcessStatus();
    }
  };

  render() {
    const {
      dirty,
      loading,
      loadingSave,
      successSave,
      handleSubmit,
      pristine,
      submitting,
      optionsValue,
      updateFilterOptionField,
      updateFilterOption,
      name,
      isDefault,
      isDefaultSearch,
      collections: selectedCollections,
      selectedPlan,
      numberOfFilterOptionsForSearch,
      numberOfFilterOptionsForCustomFilter,
      history,
      tenantApp,
      i18n
    } = this.props;
    const { filterTreeId } = this.props.match.params;
    const isEdit = !!filterTreeId;
    const { collections, activeRequestModal, activeModalResult } = this.state;

    return (
      <div className="BC-Page BC-Page__FilterTree-Item">
        <MainMenu menuId="menu-filters-filter-trees" />
        <Page
          title={
            !isEdit && name === ''
              ? i18n.translate('AdminHTML.FilterTree.Item.item_PageTitle')
              : name
          }
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.FilterTree.Item.item_PageBreadcrumbs_Content'),
              disabled: submitting,
              onAction: this.backToManagementPage
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button BC-Page__Button-HasBreadcrumb">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.backToManagementPage}>
                    {i18n.translate('AdminHTML.Common.Action.discard')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={pristine || submitting}
                  loading={loadingSave && !pristine}>
                  {i18n.translate('AdminHTML.Common.Action.save')}
                </Button>
              </Stack>
            </div>
          }>
          <form onSubmit={this.handleDefaultSubmit}>
            <Layout>
              <Layout.Section>
                <div className="BC-Container__Notifications">
                  {/* Installation reminder status banner */}
                  <InstallationRemindStatusBanner />

                  <InfoBanner tenantApp={tenantApp} sendRequest={() => this.toggleModal('activeRequestModal')} />

                  {/* Sticky notification for exceeded plan */}
                  <ExceededPlanNotification />
                </div>
              </Layout.Section>
              <Layout.Section>
                <Card title={i18n.translate('AdminHTML.FilterTree.Item.item_CardTitle')}>
                  <Card.Section>
                    {loading ? (
                      <FormLayout>
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={1} />
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={2} />
                      </FormLayout>
                    ) : (
                      <FormLayout>
                        <Field component="input" name="filterId" type="hidden" />
                        <Field component="input" name="tenantId" type="hidden" />
                        <Field component="input" name="isDefault" type="hidden" />
                        <Field component="input" name="isDefaultSearch" type="hidden" />
                        <Field
                          name={'name'}
                          component={RenderTextField}
                          label={i18n.translate('AdminHTML.FilterTree.Common.name')}
                          validate={[isRequiredValue, hasTag]}
                        />
                        {isDefault ? (
                          <p style={{ marginTop: '-0.625rem' }}>
                            <TextStyle variation="subdued">
                              {i18n.translate('AdminHTML.FilterTree.Item.item_TextStyle1_Content', {
                                t: (
                                  <Button plain onClick={this.newFilterTree}>
                                    {i18n.translate(
                                      'AdminHTML.FilterTree.Item.item_TextStyle1_Content_t'
                                    )}
                                  </Button>
                                )
                              })}
                            </TextStyle>
                          </p>
                        ) : isDefaultSearch ? (
                          <p style={{ marginTop: '-0.625rem' }}>
                            <TextStyle variation="subdued">
                              {i18n.translate('AdminHTML.FilterTree.Item.item_TextStyle2_Content', {
                                t1: (
                                  <TextStyle variation="strong">
                                    {i18n.translate(
                                      'AdminHTML.FilterTree.Item.item_TextStyle2_Content_t1'
                                    )}
                                  </TextStyle>
                                ),
                                t2: (
                                  <TextStyle variation="strong">
                                    {i18n.translate(
                                      'AdminHTML.FilterTree.Item.item_TextStyle2_Content_t2'
                                    )}
                                  </TextStyle>
                                )
                              })}
                            </TextStyle>
                          </p>
                        ) : (
                          <Field
                            name={'collections'}
                            component={RenderAutocomplete}
                            label={i18n.translate('AdminHTML.FilterTree.Common.collections')}
                            value={selectedCollections}
                            options={collections}
                            placeholder={i18n.translate(
                              'AdminHTML.FilterTree.Item.item_Field_Collections_Placeholder'
                            )}
                            textHelper={i18n.translate(
                              'AdminHTML.FilterTree.Item.item_Field_Collections_TextHelper'
                            )}
                            onChange={this.handleChangeCollections}
                            extraSearchFields={['value', 'handle']}
                          />
                        )}
                      </FormLayout>
                    )}
                  </Card.Section>
                </Card>
              </Layout.Section>
              <Layout.Section>
                <FieldArray
                  name="filterOptions"
                  loading={loading}
                  component={OptionsTable}
                  optionsValue={optionsValue}
                  isDefaultSearch={isDefaultSearch}
                  numberOfFilterOptionsForSearch={numberOfFilterOptionsForSearch}
                  numberOfFilterOptionsForCustomFilter={numberOfFilterOptionsForCustomFilter}
                  updateFilterOptionField={updateFilterOptionField}
                  updateFilterOption={updateFilterOption}
                  resetMultiLevelStates={this.resetMultiLevelStates}
                />
              </Layout.Section>
            </Layout>

            <PageActions
              primaryAction={{
                content: i18n.translate('AdminHTML.Common.Action.save'),
                onAction: handleSubmit(this.handleSave.bind(this)),
                disabled: pristine || submitting,
                loading: loadingSave && !pristine
              }}
              secondaryActions={
                !pristine
                  ? [
                      {
                        content: i18n.translate('AdminHTML.Common.Action.discard'),
                        onAction: this.backToManagementPage
                      }
                    ]
                  : []
              }
            />
          </form>

          <SendRequestFeatureModal
            active={activeRequestModal}
            handleClose={() => this.toggleModal('activeRequestModal')}
            handleOpenResultModal={() => this.toggleModal('activeModalResult')}
            featureKey="filterOptions"
          />
          <RequestResultModal
            active={activeModalResult}
            handleClose={() => this.toggleModal('activeModalResult')}
            featureName={i18n.translate(
              'AdminHTML.Common.RequestResultModal.content_t1_filterOptions'
            )}
          />

          {/* Confirm before leaving */}
          <RouteLeavingGuard
            when={dirty}
            // Navigate function
            navigate={path => history.push(path)}
            shouldBlockNavigation={dirty}
          />
        </Page>
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded type="filter-trees" conditionShow={successSave} />
        )}
      </div>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  backToManagementPage = () => {
    const { history } = this.props;
    history.push(routes.filterTree.list());
  };

  newFilterTree = () => {
    const { history } = this.props;
    history.push(routes.filterTree.new());
  };

  // Handle validate data before submit
  validateSaveOption(data) {
    //Handle collection
    const { i18n } = this.props;
    if (_.isEmpty(data.collections) && !data.isDefault && !data.isDefaultSearch) {
      throw new SubmissionError({
        collections: i18n.translate('AdminHTML.FilterTree.Item.item_SubmissionError1')
      });
    }

    return true;
  }

  async handleSave(data) {
    const { isDefault } = data;
    const { markDoneStep } = this.props;

    if (isDefault) {
      markDoneStep('V2_FILTER1', {
        isDone: true
      });
    }

    const { updateFilterTree, createFilterTree, history, resetForm } = this.props;
    const { filterTreeId } = this.props.match.params;

    // Pretty data if needed
    let prettyData = { ...data, name: prettyText(data.name) };

    if (this.validateSaveOption(prettyData)) {
      if (filterTreeId) {
        await updateFilterTree(filterTreeId, prettyData);
      } else {
        const result = await createFilterTree(prettyData);
        if (result.type === 'CREATE_FILTER_TREE_SUCCESS') {
          const filterId = _.get(result.payload, 'createdFilterTree.filterId');
          resetForm();
          if (filterId) {
            history.push(routes.filterTree.view(filterId));
          } else {
            history.push(routes.filterTree.list());
          }
        }
      }
    }
  }

  handleChangeCollections = collections => {
    this.props.updateField('collections', collections);
  };

  resetMultiLevelStates = () => {
    const { resetManualValuesMultiLevelCollections, resetManualValuesMultiLevelTags } = this.props;

    resetManualValuesMultiLevelCollections();
    resetManualValuesMultiLevelTags();
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
  listFilterTree: (q, skip, limit) =>
    dispatch(managementActions.listFilterTree(q, skip, limit, ownProps.i18n)),
  getAvailableCollections: () => dispatch(CollectionActions.getAvailableCollections(ownProps.i18n)),
  getFilterTree: (filterTreeId: string) =>
    dispatch(FilterTreeActions.getFilterTree(filterTreeId, ownProps.i18n)),
  createFilterTree: (filterTree: FilterTreeType) =>
    dispatch(FilterTreeActions.createFilterTree(filterTree, ownProps.i18n)),
  updateFilterTree: (filterTreeId: string, filterTree: FilterTreeType) =>
    dispatch(FilterTreeActions.updateFilterTree(filterTreeId, filterTree, ownProps.i18n)),
  updateField: (field, value) => dispatch(change(`FilterTreeForm`, field, value)),
  updateFilterOptionField: (index, field, value) =>
    dispatch(change(`FilterTreeForm`, `filterOptions[${index}][${field}]`, value)),
  updateFilterOption: (index, data) => {
    dispatch(change(`FilterTreeForm`, `filterOptions[${index}]`, data));
  },
  resetManualValuesMultiLevelTags: () =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelTags([])),
  resetManualValuesMultiLevelCollections: () =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelCollections([])),
  resetForm: () => dispatch(reset('FilterTreeForm')),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { filterTreeId } = props.match.params;

  const filterTree = state.filterTree.filterTree[filterTreeId];
  const formValues = getFormValues('FilterTreeForm')(state);
  const tenantId = _.get(state.oauth.tenantApp, 'tenantId', '');

  // Get limit number for filter options
  let numberOfFilterOptionsForSearch = _.get(
    state.oauth.tenantApp,
    'numberOfFilterOptionsSearch',
    LIMIT_FILTER_OPTIONS
  );
  let numberOfFilterOptionsForCustomFilter = _.get(
    state.oauth.tenantApp,
    'numberOfFilterOptions',
    LIMIT_FILTER_OPTIONS
  );
  return {
    tenantApp: _.get(state, 'oauth.tenantApp', {}),
    loading: state.filterTree.loading || state.filterData.loading,
    loadingSave: state.filterTree.loadingSave,
    success: state.filterTree.success,
    successSave: state.filterTree.successSave,
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    collectionsSuccess: state.availableCollections.success,
    availableCollections: _.get(state.availableCollections, 'availableCollections', []),
    filterTreeCollection: !!filterTree ? filterTree.collections : [],
    optionsValue: formValues && formValues.filterOptions ? formValues.filterOptions : [],
    name: formValues && formValues.name ? formValues.name : '',
    collections: formValues && formValues.collections ? formValues.collections : [],
    isDefault: !!filterTree && filterTree.isDefault,
    isDefaultSearch: !!filterTree && filterTree.isDefaultSearch,
    numberOfFilterOptionsForSearch,
    numberOfFilterOptionsForCustomFilter,
    initialValues: !!filterTree
      ? filterTree
      : {
          filterId: '',
          filterOptions: [],
          isDefault: false,
          isDefaultSearch: false,
          name: '',
          status: 'active',
          tenantId
        },
    scheduleSyncIn: _.get(state.filterTree, `scheduleSyncInfo.${filterTreeId}.scheduleSyncIn`, 0),
    waitSyncAt: _.get(state.filterTree, `scheduleSyncInfo.${filterTreeId}.waitSyncAt`, 0)
  };
};

const FilterTreeItemForm = reduxForm({
  form: 'FilterTreeForm', // a unique identifier for this form
  enableReinitialize: true
})(FilterTreeItem);

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(FilterTreeItemForm)),
  'FlowFullSync'
);
